import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useCountUp(target: number, duration = 1600, active = false): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let startTime: number | null = null;
    let raf: number;
    const tick = (ts: number) => {
      if (!startTime) startTime = ts;
      const t = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 4);
      setCount(Math.floor(eased * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, active]);
  return count;
}

function useInView(threshold = 0.25) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// ─── Chess board SVG background ───────────────────────────────────────────────
// Real SVG chess board squares at low opacity — not a CSS trick.

function ChessboardSvg({
  squares = 8,
  squareSize = 64,
  opacity = 0.09,
  className,
}: {
  squares?: number;
  squareSize?: number;
  opacity?: number;
  className?: string;
}) {
  const size = squares * squareSize;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      focusable="false"
      className={className}
      style={{ opacity }}
    >
      {Array.from({ length: squares }, (_, row) =>
        Array.from({ length: squares }, (_, col) =>
          (row + col) % 2 === 0 ? (
            <rect
              key={`${row}-${col}`}
              x={col * squareSize}
              y={row * squareSize}
              width={squareSize}
              height={squareSize}
              fill="#EEEED5"
            />
          ) : null,
        ),
      )}
    </svg>
  );
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: '♛',
    title: 'Year in Review',
    desc: 'Your complete chess season on one page. Streaks, biggest wins, time spent, and your full opening breakdown, laid out cleanly.',
    href: '/wrapped',
    cta: 'Get Your Wrapped',
    accent: '#43e97b',
    live: true,
    featured: true,
  },
  {
    icon: '⚔',
    title: 'Matchup Analyzer',
    desc: 'Enter two usernames or paste a game URL. Get win probability, recent form, opening tendencies, and a breakdown of what to watch out for.',
    href: '/matchup',
    cta: 'Analyze a Matchup',
    accent: '#38f9d7',
    live: true,
    featured: false,
  },
  {
    icon: '♜',
    title: 'Pulse',
    desc: 'Which openings are rising, which are fading? Month-over-month opening trends across tracked players, powered by a nightly dbt pipeline.',
    href: '/pulse',
    cta: 'View Pulse',
    accent: '#c77dff',
    live: true,
    featured: false,
  },
  {
    icon: '♞',
    title: 'Dashboard',
    desc: 'Your personalized Looker Studio report, embedded directly. Full game history, rating trends, and opening breakdown in one interactive view.',
    href: '/dashboard',
    cta: 'Open Dashboard',
    accent: '#f6c90e',
    live: true,
    featured: false,
  },
] as const;

const HOW_STEPS = [
  {
    num: '01',
    title: 'Enter your username',
    body: 'Any Chess.com player. No account, no setup.',
  },
  {
    num: '02',
    title: 'We fetch your games',
    body: 'Your full game history, pulled directly from the Chess.com public API.',
  },
  {
    num: '03',
    title: 'Get your analytics',
    body: 'Stats, charts, and patterns ready in about 15 seconds.',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function LandingPage() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const { ref: statsRef, inView: statsVisible } = useInView(0.3);
  const gamesCount = useCountUp(22501, 1600, statsVisible);

  return (
    <div className="lp-root">

      {/* ━━━━━━━━━━━━━━━━━━━━━━  HERO  ━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="lp-hero" aria-label="Hero">

        {/* Real SVG chess board, rotated, right side only */}
        <div className="lp-chess-wrap" aria-hidden>
          <ChessboardSvg squares={10} squareSize={64} opacity={0.08} />
        </div>

        {/* Text column */}
        <div className={`lp-hero-text${ready ? ' lp-in' : ''}`}>
          <span className="lp-pill">♟ Open source · Always free</span>

          <h1 className="lp-h1">
            Your Chess,<br />
            <span className="lp-h1-solid">Analyzed.</span>
          </h1>

          <p className="lp-hero-sub">
            Full game history, opening patterns, and opponent breakdowns
            for any Chess.com player. Enter a username and get answers in seconds.
          </p>

          <div className="lp-hero-actions">
            <Link to="/wrapped" className="cta-button">
              Get Your Wrapped
            </Link>
            <Link to="/matchup" className="lp-ghost-link">
              Analyze a Matchup →
            </Link>
          </div>

          <div className="lp-trust">
            <span>Chess.com API</span>
            <span className="lp-trust-sep" aria-hidden />
            <span>BigQuery</span>
            <span className="lp-trust-sep" aria-hidden />
            <span>dbt</span>
            <span className="lp-trust-sep" aria-hidden />
            <span>Open source</span>
          </div>
        </div>

        {/* Stat preview card — no macOS chrome, just real data layout */}
        <div className={`lp-stat-card${ready ? ' lp-in lp-in--delay' : ''}`} aria-hidden>
          <div className="lp-card-header">
            <div className="lp-card-avatar">EL</div>
            <div className="lp-card-meta">
              <span className="lp-card-user">@EdwardL903</span>
              <span className="lp-card-period">2026 · Blitz</span>
            </div>
            <span className="lp-streak-badge">🔥 4W</span>
          </div>

          <div className="lp-card-rating">
            <span className="lp-rating-big">1,847</span>
            <span className="lp-rating-delta lp-delta-up">+122 this year</span>
          </div>

          <div className="lp-card-row3">
            <div className="lp-mini-stat">
              <span className="lp-mini-val">58%</span>
              <span className="lp-mini-label">Win rate</span>
            </div>
            <div className="lp-mini-divider" />
            <div className="lp-mini-stat">
              <span className="lp-mini-val">1,203</span>
              <span className="lp-mini-label">Games</span>
            </div>
            <div className="lp-mini-divider" />
            <div className="lp-mini-stat">
              <span className="lp-mini-val">Italian</span>
              <span className="lp-mini-label">Best opening</span>
            </div>
          </div>

          <div className="lp-card-bar-section">
            <div className="lp-bar-label">Win rate</div>
            <div className="lp-bar-track" role="meter" aria-label="58% win rate">
              <div className="lp-bar-fill" style={{ width: '58%' }} />
            </div>
            <span className="lp-bar-pct">58%</span>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━  STATS STRIP  ━━━━━━━━━━━━━━━━━━━━━━ */}
      <div ref={statsRef} className="lp-stats-strip">
        <div className="lp-stats-inner">
          <div className="lp-stat">
            <span className="lp-stat-num">{statsVisible ? gamesCount.toLocaleString() : '0'}+</span>
            <span className="lp-stat-label">games in BigQuery</span>
          </div>
          <div className="lp-stat-sep" aria-hidden />
          <div className="lp-stat">
            <span className="lp-stat-num">4</span>
            <span className="lp-stat-label">analytics tools</span>
          </div>
          <div className="lp-stat-sep" aria-hidden />
          <div className="lp-stat">
            <span className="lp-stat-num">Daily</span>
            <span className="lp-stat-label">pipeline updates</span>
          </div>
          <div className="lp-stat-sep" aria-hidden />
          <div className="lp-stat">
            <span className="lp-stat-num">Free</span>
            <span className="lp-stat-label">no account needed</span>
          </div>
        </div>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━  FEATURES  ━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="lp-features" aria-label="Features">
        <div className="lp-section-head">
          <h2 className="lp-section-title">Everything you need to understand your game</h2>
          <p className="lp-section-sub">Four tools, one platform. No account required.</p>
        </div>

        {/* Asymmetric grid: Year in Review spans both rows on the left */}
        <div className="lp-feat-grid">
          {FEATURES.map((f) => (
            <article
              key={f.title}
              className={[
                'lp-feat-card',
                f.featured ? 'lp-feat-card--featured' : '',
                !f.live ? 'lp-feat-card--dim' : '',
              ].filter(Boolean).join(' ')}
              style={{ '--feat-accent': f.accent } as React.CSSProperties}
            >
              <div className="lp-feat-icon">{f.icon}</div>
              <h3 className="lp-feat-title">{f.title}</h3>
              <p className="lp-feat-desc">{f.desc}</p>
              {f.live ? (
                <Link to={f.href} className="lp-feat-cta">
                  {f.cta} →
                </Link>
              ) : (
                <span className="lp-feat-soon">{f.cta}</span>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━  HOW IT WORKS  ━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="lp-how" aria-label="How it works">
        <h2 className="lp-section-title lp-centered">How it works</h2>
        <div className="lp-how-steps">
          {HOW_STEPS.map((s) => (
            <div key={s.num} className="lp-how-step">
              <div className="lp-how-num">{s.num}</div>
              <h3 className="lp-how-title">{s.title}</h3>
              <p className="lp-how-body">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━  CTA BANNER  ━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="lp-cta-section" aria-label="Get started">
        <div className="lp-cta-chess-wrap" aria-hidden>
          <ChessboardSvg squares={8} squareSize={56} opacity={0.05} />
        </div>
        <div className="lp-cta-inner">
          <h2 className="lp-cta-title">Ready to understand your chess?</h2>
          <p className="lp-cta-sub">Just your Chess.com username. Results in about 15 seconds.</p>
          <Link to="/wrapped" className="cta-button lp-cta-btn">
            Get Your Wrapped →
          </Link>
        </div>
      </section>

    </div>
  );
}
